import apiClient from './Request'
import { baseUrls } from './../const'

const base = baseUrls['loraBaseUrl']

export default {
  // ─── Sites ───────────────────────────────────────────────────────────────
  getSites(params = {}) {
    return apiClient.get(`${base}/admin/sites`, { params })
  },
  getSite(siteId) {
    return apiClient.get(`${base}/admin/sites/${siteId}`)
  },
  createSite(data) {
    return apiClient.post(`${base}/admin/sites`, data)
  },
  updateSite(siteId, data) {
    return apiClient.patch(`${base}/admin/sites/${siteId}`, data)
  },
  getSiteDevices(siteId) {
    return apiClient.get(`${base}/admin/sites/${siteId}/devices`)
  },
  getSiteRegions() {
    return apiClient.get(`${base}/admin/sites/regions`)
  },

  // ─── Gateways ────────────────────────────────────────────────────────────
  getGateways(params = {}) {
    return apiClient.get(`${base}/admin/gateways`, { params })
  },
  getGateway(id) {
    return apiClient.get(`${base}/admin/gateways/${id}`)
  },
  createGateway(data) {
    return apiClient.post(`${base}/admin/gateways`, data)
  },
  returnGatewayToPool(id, reason) {
    return apiClient.post(`${base}/admin/gateways/${id}/return-to-pool`, {
      reason
    })
  },
  decommissionGateway(id, reason) {
    return apiClient.post(`${base}/admin/gateways/${id}/decommission`, {
      reason
    })
  },

  // ─── End Devices ─────────────────────────────────────────────────────────
  getEndDevices(params = {}) {
    return apiClient.get(`${base}/admin/end-devices`, { params })
  },
  createEndDevice(data) {
    return apiClient.post(`${base}/admin/end-devices`, data)
  },
  importEndDevicesPreview(formData) {
    return apiClient.post(`${base}/admin/end-devices/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  importEndDevicesConfirm(data) {
    return apiClient.post(`${base}/admin/end-devices/import/confirm`, data)
  },
  returnEndDeviceToPool(id, reason) {
    return apiClient.post(`${base}/admin/end-devices/${id}/return-to-pool`, {
      reason
    })
  },
  decommissionEndDevice(id, reason) {
    return apiClient.post(`${base}/admin/end-devices/${id}/decommission`, {
      reason
    })
  },

  // ─── Smartz Pods ────────────────────────────────────────────────────
  getBleDevices(params = {}) {
    const status = typeof params === 'string' ? params : params.status
    return apiClient.get(`${base}/admin/ble-devices`, {
      params: status ? { status } : {}
    })
  },
  createBleDevice(data) {
    return apiClient.post(`${base}/admin/ble-devices`, data)
  },
  deleteBleDevice(id) {
    return apiClient.delete(`${base}/admin/ble-devices/${id}`)
  },

  // ─── Minew Nodes ────────────────────────────────────────────────────
  getNodes(params = {}) {
    return apiClient.get(`${base}/admin/nodes`, { params })
  },
  createNode(data) {
    return apiClient.post(`${base}/admin/nodes`, data)
  },
  deleteNode(id) {
    return apiClient.delete(`${base}/admin/nodes/${id}`)
  },

  // ─── Dashboard Displays ──────────────────────────────────────────────
  getDashboardDisplays(params = {}) {
    return apiClient.get(`${base}/admin/dashboard-displays`, { params })
  },
  createDashboardDisplay(data) {
    return apiClient.post(`${base}/admin/dashboard-displays`, data)
  },
  deleteDashboardDisplay(guid) {
    return apiClient.delete(`${base}/admin/dashboard-displays/${guid}`)
  },

  // ─── Jobs ────────────────────────────────────────────────────────────────
  getJobs(params = {}) {
    return apiClient.get(`${base}/admin/jobs`, { params })
  },
  getJob(jobId) {
    return apiClient.get(`${base}/admin/jobs/${jobId}`)
  },
  createJob(siteId, data) {
    return apiClient.post(`${base}/admin/sites/${siteId}/jobs`, data)
  },
  updateJobSchedule(jobId, data) {
    return apiClient.patch(`${base}/admin/jobs/${jobId}/schedule`, data)
  },
  deleteJob(jobId) {
    return apiClient.delete(`${base}/admin/jobs/${jobId}`)
  },
  publishJob(jobId) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/publish`)
  },
  getPendingPqJobs() {
    return apiClient.get(`${base}/admin/jobs/pending-pq`)
  },
  getEscalatedJobs() {
    return apiClient.get(`${base}/admin/jobs/escalated`)
  },
  acknowledgeEscalation(jobId) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/escalation/acknowledge`)
  },
  getPqMetrics(jobId) {
    return apiClient.get(`${base}/admin/jobs/${jobId}/pq-metrics`)
  },
  pqSignoff(jobId, data) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/pq-signoff`, data)
  },
  getJobIssues(jobId) {
    return apiClient.get(`${base}/admin/jobs/${jobId}/issues`)
  },
  reportIssue(jobId, data) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/issues`, data)
  },
  resolveIssue(jobId, issueId, data) {
    return apiClient.post(
      `${base}/admin/jobs/${jobId}/issues/${issueId}/resolve`,
      data || {}
    )
  },
  getOpenIssues() {
    return apiClient.get(`${base}/admin/issues/open`)
  },

  // ─── Device Assignment ───────────────────────────────────────────────────
  assignGateway(jobId, gatewayId, friendlyName) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/gateways/${gatewayId}`, {
      friendlyName: friendlyName || null
    })
  },
  unassignGateway(jobId, gatewayId) {
    return apiClient.delete(`${base}/admin/jobs/${jobId}/gateways/${gatewayId}`)
  },
  assignEndDevice(jobId, endDeviceId, friendlyName) {
    return apiClient.post(
      `${base}/admin/jobs/${jobId}/end-devices/${endDeviceId}`,
      { friendlyName: friendlyName || null }
    )
  },
  unassignEndDevice(jobId, endDeviceId) {
    return apiClient.delete(
      `${base}/admin/jobs/${jobId}/end-devices/${endDeviceId}`
    )
  },
  assignBleDevice(jobId, data) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/ble-devices`, data)
  },
  unassignBleDevice(jobId, bleDeviceId) {
    return apiClient.delete(
      `${base}/admin/jobs/${jobId}/ble-devices/${bleDeviceId}`
    )
  },
  assignNode(jobId, data) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/nodes`, data)
  },
  unassignNode(jobId, nodeId) {
    return apiClient.delete(`${base}/admin/jobs/${jobId}/nodes/${nodeId}`)
  },
  assignDashboardDisplay(jobId, data) {
    return apiClient.post(
      `${base}/admin/jobs/${jobId}/dashboard-displays`,
      data
    )
  },
  unassignDashboardDisplay(jobId, displayId) {
    return apiClient.delete(
      `${base}/admin/jobs/${jobId}/dashboard-displays/${displayId}`
    )
  },

  // ─── Installers ──────────────────────────────────────────────────────────
  getAvailableInstallers() {
    return apiClient.get(`${base}/admin/users?role=Installer`)
  },
  getAllInstallers() {
    return apiClient.get(`${base}/admin/users?role=Installer`)
  },
  resetInstallerPassword(userId) {
    return apiClient.post(`${base}/admin/users/${userId}/reset-password`)
  },

  // ─── Qualification ───────────────────────────────────────────────────────
  getJobQualification(jobId) {
    return apiClient.get(`${base}/admin/jobs/${jobId}/qualification`)
  },
  getDeviceQualification(jobId, deviceType, deviceId) {
    return apiClient.get(
      `${base}/admin/jobs/${jobId}/devices/${deviceType}/${deviceId}/qualification`
    )
  },

  assignInstaller(jobId, userId) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/installers`, { userId })
  },
  unassignInstaller(jobId, userId) {
    return apiClient.delete(`${base}/admin/jobs/${jobId}/installers/${userId}`)
  },
  createAndInviteInstaller(jobId, data) {
    return apiClient.post(`${base}/admin/jobs/${jobId}/installers`, data)
  },

  // ─── Auth ─────────────────────────────────────────────────────────────────
  getUserInfo() {
    return apiClient.get(`${base}/admin/auth/me`)
  }
}
